import type {
  Application,
  AutomationConfig,
  InputConfig,
} from "@smartsuite/automation-engine-api";
import { findInputAndPick, pickValueDataString } from "../../../utils/inputs";
import {
  isSmartSuiteIntegration,
  TRIGGERS_WITH_APPLICATIONS,
} from "../../../utils/constants";

export interface ApplicationGroup {
  appName: string;
  automations: AutomationConfig[];
}

const OTHER_APP_NAME = "Other";

export const getApplicationId = (inputs: InputConfig[]): string => {
  const applicationId = findInputAndPick(
    inputs,
    "application-id",
    pickValueDataString,
  );
  return String(applicationId);
};

export const findApplicationById = (
  applications: Application[],
  applicationId: string,
): string => {
  const application = applications.find((app) => app.id === applicationId);
  return application?.label ?? OTHER_APP_NAME;
};

export const getGroupedAutomations = (
  automations: AutomationConfig[],
  applications: Application[],
): Map<string, AutomationConfig[]> => {
  const result = new Map<string, AutomationConfig[]>();
  automations.forEach((automation) => {
    const integrationId = automation.trigger?.triggerReference?.integrationId;
    if (integrationId !== undefined && isSmartSuiteIntegration(integrationId)) {
      if (automation.trigger != null) {
        const appId = getApplicationId(automation.trigger.inputs);
        const appName = findApplicationById(applications, String(appId));
        if (automation.trigger.triggerReference === undefined) {
          return;
        }
        if (
          appName === OTHER_APP_NAME &&
          TRIGGERS_WITH_APPLICATIONS.includes(
            automation.trigger.triggerReference?.triggerId,
          )
        ) {
          return;
        }
        if (result.has(appName)) {
          result.set(appName, [...(result.get(appName) ?? []), automation]);
        } else {
          result.set(appName, [automation]);
        }
      }
    } else {
      result.set("Integrations", [
        ...(result.get("Integrations") ?? []),
        automation,
      ]);
    }
  });

  return result;
};

export const getOrderedAutomations = (
  applications: Application[],
  applicationGroup: ApplicationGroup[],
): ApplicationGroup[] => {
  let result = [...applicationGroup];

  for (const application of applications) {
    if (
      result.find(({ appName }) => appName === application.label) === undefined
    ) {
      result.push({ appName: application.label, automations: [] });
    }
  }

  result = result.map((value) => {
    return {
      ...value,
      automations: value.automations.sort(({ label: a }, { label: b }) => {
        return a.localeCompare(b);
      }),
    };
  });

  return result.sort(({ appName: a }, { appName: b }) => {
    return a.localeCompare(b);
  });
};
